import React from 'react';
import { FiUsers,  FiCheckCircle,   FiBarChart2, FiHeart, FiMessageSquare, FiGlobe, FiUserCheck } from 'react-icons/fi';

const CommunityEngagement = () => {
  const engagementFeatures = [
    {
      icon: <FiMessageSquare className="w-6 h-6" />,
      title: 'Real-Time Communication Between All Stakeholders',
      description: 'Modern ERPs enable seamless communication between teachers, parents, students, and administrators. Announcements, messages, and updates reach everyone instantly. No more WhatsApp groups or missed information. Everything is centralized and trackable.',
      stat: '85% of parents prefer institution-managed communication channels',
      color: 'bg-indigo-50'
    },
    {
      icon: <FiUserCheck className="w-6 h-6" />,
      title: 'Parent Portal for Active Involvement',
      description: 'Parents can view attendance, grades, assignments, and announcements in real-time. They feel connected to their child\'s education and can partner with teachers more effectively. This active involvement leads to better student outcomes.',
      stat: '90% of parents want real-time access to their child\'s academic progress',
      color: 'bg-emerald-50'
    },
    {
      icon: <FiUsers className="w-6 h-6" />,
      title: 'Student Portal for Ownership of Learning',
      description: 'Students can access their schedules, assignments, grades, and learning materials. They take ownership of their education and stay organized. This builds responsibility and self-motivation.',
      stat: '75% of students feel more motivated when they can track their own progress',
      color: 'bg-amber-50'
    },
    {
      icon: <FiHeart className="w-6 h-6" />,
      title: 'Faculty Collaboration Tools',
      description: 'Teachers can collaborate with colleagues, share resources, and coordinate across departments. This builds a professional community and improves teaching quality. No more isolated classrooms.',
      stat: '80% of teachers value collaboration with colleagues',
      color: 'bg-rose-50'
    },
    {
      icon: <FiGlobe className="w-6 h-6" />,
      title: 'Alumni and Community Engagement',
      description: 'Modern ERPs extend beyond current students to include alumni engagement. Alumni can stay connected, share success stories, and mentor current students. This builds a lifelong community.',
      stat: '60% of institutions are investing in alumni engagement',
      color: 'bg-purple-50'
    },
    {
      icon: <FiBarChart2 className="w-6 h-6" />,
      title: 'Data-Driven Community Insights',
      description: 'Track engagement metrics—parent portal logins, message read rates, event attendance. Understand how your community is interacting and identify areas for improvement.',
      stat: '70% of institutions use engagement data to improve community building',
      color: 'bg-blue-50'
    }
  ];

  const engagementBenefits = [
    {
      title: 'Parents Feel Connected and Informed',
      description: 'Regular updates and easy access to information reduce anxiety and build trust.'
    },
    {
      title: 'Students Take Ownership of Their Learning',
      description: 'Access to schedules, grades, and assignments empowers students to take responsibility.'
    },
    {
      title: 'Teachers Feel Supported and Valued',
      description: 'Collaboration tools and communication channels help teachers work together effectively.'
    },
    {
      title: 'Administrators Have Complete Visibility',
      description: 'Track engagement across all stakeholders and identify areas needing attention.'
    },
    {
      title: 'Community Pride and Loyalty',
      description: 'When everyone is connected, the institution becomes a true community that people are proud to be part of.'
    }
  ];

  return (
    <>
      {/* Introduction */}
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        An educational institution is more than a place of learning—it's a community. Students, parents, teachers, administrators, and alumni all play vital roles. When this community is connected and engaged, the institution thrives. When it's disconnected, everyone suffers.
      </p>
      
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        <strong className="text-slate-900">Modern educational ERPs are transforming community engagement.</strong> They connect parents, students, faculty, and administrators in ways that were impossible just a few years ago. They build stronger, more connected communities.
      </p>

      <p className="text-lg text-slate-600 leading-relaxed mb-8">
        Here's how a modern ERP builds stronger communities through parent, student, and faculty engagement.
      </p>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-indigo-600">85%</div>
          <div className="text-sm text-slate-500">Parents prefer institution-managed communication</div>
        </div>
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-emerald-600">90%</div>
          <div className="text-sm text-slate-500">Parents want real-time access to academic progress</div>
        </div>
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-amber-600">80%</div>
          <div className="text-sm text-slate-500">Teachers value collaboration with colleagues</div>
        </div>
      </div>

      <h2 className="text-2xl font-bold text-slate-900 mb-6">How a Modern ERP Builds Community Engagement</h2>

      {/* Engagement Features */}
      {engagementFeatures.map((item, i) => (
        <div key={i} className={`${item.color} rounded-2xl p-6 mb-6 border`}>
          <div className="flex items-start gap-4">
            <div className="bg-white rounded-xl p-3 shadow-sm flex-shrink-0 mt-1">
              {item.icon}
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-slate-900 mb-2">{item.title}</h3>
              <p className="text-slate-600 leading-relaxed text-sm mb-4">{item.description}</p>
              <div className="bg-white/60 rounded-xl p-3 border border-amber-200">
                <p className="text-sm font-semibold text-amber-600">📊 {item.stat}</p>
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* Benefits Section */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
        <h3 className="text-xl font-bold text-slate-900 mb-4">What Community Engagement Delivers</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {engagementBenefits.map((item, i) => (
            <div key={i} className="bg-indigo-50 rounded-xl p-4 border border-indigo-200">
              <div className="flex items-start gap-3">
                <div className="bg-white rounded-lg p-2 flex-shrink-0">
                  <FiCheckCircle className="w-4 h-4 text-emerald-500" />
                </div>
                <div>
                  <h4 className="font-bold text-slate-900 text-sm">{item.title}</h4>
                  <p className="text-xs text-slate-600">{item.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Comparison Section */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
        <h3 className="text-xl font-bold text-slate-900 mb-4">Disconnected vs. Connected Community</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-red-50 rounded-xl p-4 border border-red-200">
            <div className="text-sm font-semibold text-red-700 mb-2">Disconnected Community</div>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-start gap-2">• <span>Parents are frustrated and uninformed</span></li>
              <li className="flex items-start gap-2">• <span>Students are disengaged</span></li>
              <li className="flex items-start gap-2">• <span>Teachers work in isolation</span></li>
              <li className="flex items-start gap-2">• <span>Low trust and loyalty</span></li>
              <li className="flex items-start gap-2">• <span>High attrition of students and staff</span></li>
            </ul>
          </div>
          <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
            <div className="text-sm font-semibold text-emerald-700 mb-2">Connected Community with Truyon</div>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-start gap-2">• <span>Parents are informed and engaged</span></li>
              <li className="flex items-start gap-2">• <span>Students are motivated and responsible</span></li>
              <li className="flex items-start gap-2">• <span>Teachers collaborate and support each other</span></li>
              <li className="flex items-start gap-2">• <span>Strong trust and loyalty</span></li>
              <li className="flex items-start gap-2">• <span>Retention improves across the board</span></li>
            </ul>
          </div>
        </div>
      </div>

      {/* The Community Story */}
      <div className="mt-8 bg-amber-50 rounded-2xl p-6 border border-amber-200">
        <h3 className="text-lg font-bold text-amber-900 mb-3">The Power of Community</h3>
        <p className="text-amber-800 leading-relaxed mb-3">
          When parents are engaged, students do better. When teachers collaborate, teaching improves. When the whole community is connected, the institution becomes more than a school or college—it becomes a community of learning and growth.
        </p>
        <p className="text-amber-800 leading-relaxed mb-3">
          Technology can help build this community. But it's not just about the technology—it's about what the technology enables. Connection, communication, collaboration, and trust. These are the building blocks of a strong institution.
        </p>
        <p className="text-amber-800 leading-relaxed">
          <strong>Community is not built by buildings. It's built by connection.</strong>
        </p>
      </div>

      {/* Why Truyon Section */}
      <div className="mt-8 bg-indigo-50 rounded-2xl p-6 border border-indigo-200">
        <h3 className="text-lg font-bold text-indigo-900 mb-3">Why Truyon Builds Stronger Communities</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">💬</div>
            <h4 className="font-bold text-slate-900 text-sm">Real-Time Communication</h4>
            <p className="text-xs text-slate-600">Connect everyone instantly</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">👨‍👩‍👦</div>
            <h4 className="font-bold text-slate-900 text-sm">Parent Engagement</h4>
            <p className="text-xs text-slate-600">Active parent involvement in education</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">🎓</div>
            <h4 className="font-bold text-slate-900 text-sm">Student Ownership</h4>
            <p className="text-xs text-slate-600">Students take charge of their learning</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">👩‍🏫</div>
            <h4 className="font-bold text-slate-900 text-sm">Faculty Collaboration</h4>
            <p className="text-xs text-slate-600">Teachers work together effectively</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">🌟</div>
            <h4 className="font-bold text-slate-900 text-sm">Alumni Connection</h4>
            <p className="text-xs text-slate-600">Lifelong community engagement</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📊</div>
            <h4 className="font-bold text-slate-900 text-sm">Engagement Analytics</h4>
            <p className="text-xs text-slate-600">Track and improve community connection</p>
          </div>
        </div>
      </div>

      {/* Conclusion */}
      <div className="mt-8 bg-indigo-50 rounded-2xl p-6 border border-indigo-200">
        <h3 className="text-lg font-bold text-indigo-900 mb-2">Build a Stronger Community with Truyon</h3>
        <p className="text-indigo-800 leading-relaxed">
          Your institution is a community. Every day, it brings together parents, students, teachers, and administrators. When this community is connected and engaged, everyone thrives. Students succeed. Parents trust. Teachers collaborate. The institution grows stronger.
        </p>
        <p className="text-indigo-800 leading-relaxed mt-3">
          Truyon builds stronger communities through real-time communication, parent engagement, student ownership, and faculty collaboration. All in one platform. All connected. All working together.
        </p>
        <p className="text-indigo-800 leading-relaxed mt-3">
          <strong>Truyon for community engagement.</strong> Build a community that lasts a lifetime.
        </p>
      </div>
    </>
  );
};

export default CommunityEngagement;